// How a folder's contents are ORDERED (John, 18 oct): one sort for the grid and
// the list, chosen from a quiet `sorted by <b>last touched</b> ⌄` line under the
// view chips, and turned over by pressing the selected view chip again (its
// arrow says which way: ↓ newest/most first, ↑ the other end). Pure; the shell
// keeps the choice per viewer (localStorage) and DirectoryView applies it.

import type { OsNode } from './tree';

export type FolderSortKey = 'touched' | 'added' | 'name' | 'kind';
export type SortDir = 'desc' | 'asc';
export interface FolderSort {
  key: FolderSortKey;
  dir: SortDir;
}

/** the views a sort applies to (orbit, story, timeline, tasks and repo keep their own order) */
export const SORTABLE_VIEWS: ReadonlySet<string> = new Set(['grid', 'list']);

export const SORT_OPTIONS: readonly { id: FolderSortKey; label: string }[] = [
  { id: 'touched', label: 'last touched' },
  { id: 'added', label: 'date added' },
  { id: 'name', label: 'name' },
  { id: 'kind', label: 'kind' },
];

export const DEFAULT_SORT: FolderSort = { key: 'touched', dir: 'desc' };

/** what each direction reads as, for the chip's label and the arrow's name */
export function dirWords(s: FolderSort): string {
  if (s.key === 'name') return s.dir === 'asc' ? 'a to z' : 'z to a';
  if (s.key === 'kind') return s.dir === 'desc' ? 'folders first' : 'pages first';
  return s.dir === 'desc' ? 'newest first' : 'oldest first';
}

/** a key's natural first direction: names read a to z, everything else newest/folders first */
export function naturalDir(key: FolderSortKey): SortDir {
  return key === 'name' ? 'asc' : 'desc';
}

export function flip(s: FolderSort): FolderSort {
  return { ...s, dir: s.dir === 'desc' ? 'asc' : 'desc' };
}

const KIND_RANK = (n: Pick<OsNode, 'icon' | 'mark'>): number => (n.icon === 'folder' ? 2 : n.mark === 'skill' ? 1 : 0);
const time = (v: string | null | undefined): number | null => {
  const t = v ? Date.parse(v) : NaN;
  return Number.isFinite(t) ? t : null;
};

/** Order nodes by the sort. `desc` puts the newest, the folders, or z first.
 *  Undated items always sink to the end, whichever way it turns; ties fall back
 *  to the name, a to z, so the order never shuffles between renders. */
export function sortNodes<T extends Pick<OsNode, 'name' | 'icon' | 'mark' | 'lastActiveAt' | 'createdAt'>>(nodes: readonly T[], s: FolderSort): T[] {
  const sign = s.dir === 'desc' ? -1 : 1;
  const byName = (a: T, b: T) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true });
  return [...nodes].sort((a, b) => {
    if (s.key === 'name') return sign * byName(a, b);
    if (s.key === 'kind') return sign * (KIND_RANK(a) - KIND_RANK(b)) || byName(a, b);
    const ta = time(s.key === 'added' ? a.createdAt ?? a.lastActiveAt : a.lastActiveAt);
    const tb = time(s.key === 'added' ? b.createdAt ?? b.lastActiveAt : b.lastActiveAt);
    if (ta === null && tb === null) return byName(a, b);
    if (ta === null) return 1;
    if (tb === null) return -1;
    return sign * (ta - tb) || byName(a, b);
  });
}

const KEYS = new Set<FolderSortKey>(SORT_OPTIONS.map((o) => o.id));

/** a stored sort, or the default when it is missing or malformed */
export function readSort(raw: string | null | undefined): FolderSort {
  try {
    const v = JSON.parse(String(raw ?? '')) as Partial<FolderSort>;
    if (v && KEYS.has(v.key as FolderSortKey) && (v.dir === 'asc' || v.dir === 'desc')) return { key: v.key as FolderSortKey, dir: v.dir };
  } catch {
    /* the default */
  }
  return DEFAULT_SORT;
}
